// ============================================================================
// RAID LINK PICKER
//
// Search-select rather than drag: the surfaces these links point at are
// table rows, links span six entity kinds including other RAID items, and this
// gets screen-shared in a workshop rather than fiddled with.
//
// Everything except a raid_item link carries its session, because story ids
// collide across personas and NFR/feature/ADR ids are only session-unique.
// ============================================================================

import React, { useMemo, useState } from 'react';
import { Search, X, Link2 } from 'lucide-react';
import { RaidLink, RaidLinkRelation, RaidLinkTargetType } from '../../../types/raid';
import { LINK_TARGET_LABELS, RAID_TYPES } from './raidConfig';

/** One thing a RAID item can be linked to. */
export interface LinkCandidate {
  targetType: RaidLinkTargetType;
  targetId: string;
  targetSessionId?: string;
  targetPersona?: string;
  label: string;
  /** Extra context shown under the label, e.g. the persona or RAID type. */
  sublabel?: string;
}

export interface RaidLinkPickerProps {
  candidates: LinkCandidate[];
  value: RaidLink[];
  onChange: (links: RaidLink[]) => void;
}

const RELATIONS: { value: RaidLinkRelation; label: string }[] = [
  { value: 'relates_to', label: 'relates to' },
  { value: 'blocks', label: 'blocks' },
  { value: 'derived_from', label: 'derived from' },
];

function sameLink(a: RaidLink, b: { targetType: string; targetId: string; targetSessionId?: string }) {
  return a.targetType === b.targetType
    && a.targetId === b.targetId
    && (a.targetSessionId ?? undefined) === (b.targetSessionId ?? undefined);
}

export function RaidLinkPicker({ candidates, value, onChange }: Readonly<RaidLinkPickerProps>) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const unlinked = candidates.filter(c => !value.some(v => sameLink(v, c)));
    if (!q) return unlinked.slice(0, 8);
    return unlinked
      .filter(c => c.label.toLowerCase().includes(q)
        || c.targetId.toLowerCase().includes(q)
        || (c.sublabel ?? '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [candidates, value, query]);

  const add = (c: LinkCandidate) => {
    onChange([...value, {
      targetType: c.targetType,
      targetId: c.targetId,
      ...(c.targetSessionId ? { targetSessionId: c.targetSessionId } : {}),
      ...(c.targetPersona ? { targetPersona: c.targetPersona } : {}),
      relation: 'relates_to',
      label: c.label,
    }]);
    setQuery('');
    setOpen(false);
  };

  const remove = (link: RaidLink) => onChange(value.filter(l => !sameLink(l, link)));

  const setRelation = (link: RaidLink, relation: RaidLinkRelation) =>
    onChange(value.map(l => (sameLink(l, link) ? { ...l, relation } : l)));

  return (
    <div>
      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
        <Link2 size={11} />Linked to
      </label>

      {value.length > 0 && (
        <div className="flex flex-col gap-1.5 mb-2">
          {value.map(link => (
            <div key={`${link.targetType}-${link.targetSessionId ?? ''}-${link.targetId}`}
              className="flex items-center gap-2 px-2 py-1.5 rounded-xl border border-[#e2e8f0] bg-slate-50">
              <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[9px] font-semibold text-slate-500 shrink-0">
                {LINK_TARGET_LABELS[link.targetType] ?? link.targetType}
              </span>
              <select
                value={link.relation ?? 'relates_to'}
                onChange={e => setRelation(link, e.target.value as RaidLinkRelation)}
                aria-label="Relationship"
                className="text-[10px] text-slate-500 bg-transparent outline-none cursor-pointer shrink-0"
              >
                {RELATIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <span className="flex-1 min-w-0 truncate text-xs text-slate-700" title={link.label ?? link.targetId}>
                {link.label ?? link.targetId}
              </span>
              <button
                type="button"
                onClick={() => remove(link)}
                aria-label={`Unlink ${link.label ?? link.targetId}`}
                className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors shrink-0"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="relative">
        <div className="flex items-center gap-2 border border-[#e2e8f0] rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-primary">
          <Search size={12} className="text-slate-400 shrink-0" />
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search stories, NFRs, features, ADRs or other RAID items"
            className="flex-1 text-xs text-navy placeholder:text-slate-400 outline-none bg-transparent"
          />
        </div>

        {open && matches.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-lg py-1 max-h-56 overflow-y-auto">
            {matches.map(c => (
              <button
                key={`${c.targetType}-${c.targetSessionId ?? ''}-${c.targetId}`}
                type="button"
                onClick={() => add(c)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors flex items-start gap-2"
              >
                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[9px] font-semibold text-slate-500 shrink-0 mt-0.5">
                  {LINK_TARGET_LABELS[c.targetType] ?? c.targetType}
                </span>
                <span className="min-w-0">
                  <span className="block text-xs text-slate-700 truncate">{c.label}</span>
                  {c.sublabel && <span className="block text-[10px] text-slate-400 truncate">{c.sublabel}</span>}
                </span>
              </button>
            ))}
          </div>
        )}
        {open && query.trim() && matches.length === 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2">
            <p className="text-xs text-slate-400">Nothing matches “{query}”.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/** Turns a RAID item into a candidate, so items can be linked to each other. */
export function raidItemCandidate(item: { raidId: string; title: string; type: keyof typeof RAID_TYPES }): LinkCandidate {
  return {
    targetType: 'raid_item',
    targetId: item.raidId,
    label: item.title,
    sublabel: RAID_TYPES[item.type].label,
  };
}
